<template>
  <div>
    <StackedHorizontalBarChart
      :title="$t('activityByLocation')"
      :filterContext="filterContext"
      :dataProviders="dataProviders"
      :contextUpdaters="contextUpdaters"
      :navigationConfig="navigationConfig"
      :chartFontColor="chartFontColor"
      @updateFilter="handleFilterUpdate"
      :currentLevel="currentLevel"
      :dataLoaded="dataLoaded"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import StackedHorizontalBarChart from '@/components/charts/base/StackedHorizontalBarChart.vue';

const props = defineProps({
  filterContext: {
    type: Object,
    required: true
  },
  dataLoaded: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['updateFilter']);

const store = useStore();
const { t } = useI18n();
const chartFontColor = computed(() => store.getters.chartFontColor);
const conn = store.getters.getDBConnection;

const chartName = 'statusHistoryLocationStackedBarChart';
const currentLevel = ref(0);

const getDrawingSetData = async () => {
  const query = `
    SELECT 
      d.drawingSetId AS id,
      d.drawingSetId AS parentId,
      d.drawingSetTitle AS title,
      s.title AS status_title,
      s.color AS color,
      COUNT(*) AS value,
      MIN(s.sort) as sort
    FROM filteredStatusHistory sh
    JOIN locations l ON sh.locationId = l.id
    JOIN drawings d ON l.drawingId = d.id
    JOIN status_templates s ON sh.statusId = s.id
    GROUP BY d.drawingSetId, d.drawingSetTitle, s.title, s.color
    ORDER BY d.drawingSetTitle, sort, s.title
  `;
  
  return executeAndProcessQuery(query);
};

const getDrawingData = async () => {
  const query = `
    SELECT 
      d.id AS id,
      d.drawingSetId AS parentId,
      d.title AS title,
      s.title AS status_title,
      s.color AS color,
      COUNT(*) AS value,
      MIN(s.sort) as sort
    FROM filteredStatusHistory sh
    JOIN locations l ON sh.locationId = l.id
    JOIN drawings d ON l.drawingId = d.id
    JOIN status_templates s ON sh.statusId = s.id
    GROUP BY d.id, d.drawingSetId, d.title, s.title, s.color
    ORDER BY d.title, sort, s.title
  `;
  
  return executeAndProcessQuery(query);
};

const getLocationData = async () => {
  const query = `
    SELECT 
      l.id AS id,
      l.drawingId AS parentId,
      l.title AS title,
      s.title AS status_title,
      s.color AS color,
      COUNT(*) AS value,
      MIN(s.sort) as sort
    FROM filteredStatusHistory sh
    JOIN locations l ON sh.locationId = l.id
    JOIN status_templates s ON sh.statusId = s.id
    GROUP BY l.id, l.drawingId, l.title, s.title, s.color
    ORDER BY l.title, sort, s.title
  `;
  
  return executeAndProcessQuery(query);
};

const getStatusData = async () => {
  const query = `
    SELECT
      s.id AS id,
      sh.locationId AS parentId,
      s.title AS title,
      s.title AS status_title,
      s.color AS color,
      COUNT(*) AS value,
      MIN(s.sort) as sort
    FROM filteredStatusHistory sh
    JOIN status_templates s ON sh.statusId = s.id
    GROUP BY s.id, sh.locationId, s.title, s.color, s.sort
    ORDER BY s.sort, s.title
  `;

  return executeAndProcessQuery(query);
};

const getSingleStatusData = async () => {
  const query = `
    SELECT
      s.id AS id,
      s.title AS title,
      s.title AS status_title,
      s.color AS color,
      COUNT(*) AS value,
      MIN(s.sort) as sort
    FROM filteredStatusHistory sh
    JOIN status_templates s ON sh.statusId = s.id
    GROUP BY s.id, s.title, s.color, s.sort
    ORDER BY s.sort, s.title
  `;

  return executeAndProcessQuery(query);
};

const getDrawingSetFilterContext = async (drawingSetId) => {
  const query = `
    SELECT DISTINCT sh.subItemId, sh.validFrom, sh.statusId
    FROM status_history_temporal sh
    JOIN locations l ON sh.locationId = l.id
    JOIN drawings d ON l.drawingId = d.id
    WHERE d.drawingSetId = '${drawingSetId}'
  `;
  
  await updateFilteredItemsContext(query);
};

const getDrawingFilterContext = async (drawingId) => {
  const query = `
    SELECT DISTINCT sh.subItemId, sh.validFrom, sh.statusId
    FROM status_history_temporal sh
    JOIN locations l ON sh.locationId = l.id
    WHERE l.drawingId = '${drawingId}'
  `;
  
  await updateFilteredItemsContext(query);
};

const getLocationFilterContext = async (locationId) => {
  const query = `
    SELECT DISTINCT sh.subItemId, sh.validFrom, sh.statusId
    FROM status_history_temporal sh
    WHERE sh.locationId = '${locationId}'
  `;

  await updateFilteredItemsContext(query);
};

const getStatusFilterContext = async (statusId, locationId) => {
  const query = `
    SELECT DISTINCT sh.subItemId, sh.validFrom, sh.statusId
    FROM status_history_temporal sh
    WHERE sh.statusId = '${statusId}' AND sh.locationId = '${locationId}'
  `;

  await updateFilteredItemsContext(query);
};

const resetFilterContext = async () => {
  await props.filterContext.removeFilter(chartName);
};

const dataProviders = {
  drawingSet: getDrawingSetData,
  drawing: getDrawingData,
  location: getLocationData,
  status: getStatusData,
  singlestatus: getSingleStatusData
};

const contextUpdaters = {
  drawingSet: getDrawingSetFilterContext,
  drawing: getDrawingFilterContext,
  location: getLocationFilterContext,
  status: getStatusFilterContext,
  reset: resetFilterContext
};

const navigationConfig = [
  { level: 'drawingSet', titleKey: 'drawingSets' },
  { level: 'drawing', titleKey: 'drawings' },
  { level: 'location', titleKey: 'locations' },
  { level: 'status', titleKey: 'status' },
  { level: 'singlestatus', titleKey: 'singlestatus' }
];

const updateFilteredItemsContext = async (query) => {
  await props.filterContext.updateFilterWithQuery(chartName, query);
};

const handleFilterUpdate = (chartId, filteredContext) => {
  emit('updateFilter', chartId, filteredContext);
};

// Shared data processing utility function
const processChartData = (rows) => {
  const groupedData = {};

  rows.forEach(row => {
    const id = row['id'];
    const title = row['title'];
    const parentId = row['parentId'] ? row['parentId'] : null;
    
    if (!groupedData[id]) {
      groupedData[id] = {
        name: title,
        id: id,
        parentId: parentId,
        children: []
      };
    }
    
    // Find if this color already exists in the children
    const existingColor = groupedData[id].children.find(
      child => child.color === row.color
    );
    
    if (existingColor) {
      existingColor.value += row.value;
      // Store the status names by color
      if (!existingColor.statusCounts) {
        existingColor.statusCounts = {};
      }
      existingColor.statusCounts[row.status_title] = row.value;
    } else {
      groupedData[id].children.push({
        color: row.color,
        value: row.value,
        statusName: row.status_title,
        sort: row.sort,
        statusCounts: { [row.status_title]: row.value }
      });
    }
  });

  return Object.values(groupedData).map(item => ({
    ...item,
    children: item.children.sort((a, b) => a.sort - b.sort)
  }));
};

// Generic query execution function
const executeAndProcessQuery = async (query) => {
  const result = await conn.query(query);
  const rows = result.toArray().map((row) => {
    const jsonRow = row.toJSON();
    return {
      ...jsonRow,
      value: Number(jsonRow.value)
    };
  });

  return processChartData(rows);
};
</script>